import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle, ArrowRight, Search, FolderOpen, TrendingUp, Zap, Play } from "lucide-react";

export const AboutPage = () => {
  return (
    <div>
      {/* Hero */}
      <section className="py-20 bg-slate-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-4xl sm:text-5xl text-[#2D4A6D] mb-6 font-serif">The Approach</h1>
          <div className="w-16 h-0.5 bg-[#2D4A6D] mx-auto mb-6" />
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">Operational infrastructure built around the way the business actually works.</p>
        </div>
      </section>

      {/* Operating Principles */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl text-[#2D4A6D] mb-4 font-serif">Operating Principles</h2>
            <div className="w-16 h-0.5 bg-[#2D4A6D] mx-auto" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { title: "Proactive Problem-Solving", desc: "Identify gaps early, address friction before it compounds, and keep execution moving." },
              { title: "Systems Thinking", desc: "Connect processes, tools, information, and people into workflows that function as a complete operation." },
              { title: "Operational Ownership", desc: "Execution is measured by completion, follow-through, visibility, and outcome — not simply task activity." },
              { title: "Continuous Optimization", desc: "Processes and systems evolve as the business changes. Operations should improve continuously rather than remain static." }
            ].map((item, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}>
                <div className="h-full p-6 bg-white border border-slate-200 hover:border-[#2D4A6D]/30 transition-colors">
                  <CheckCircle className="w-6 h-6 text-[#2D4A6D] mb-4" />
                  <h4 className="text-base font-semibold text-[#2D4A6D] mb-2">{item.title}</h4>
                  <p className="text-sm text-slate-600">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Methodology */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl text-[#2D4A6D] mb-4 font-serif">Methodology</h2>
            <div className="w-16 h-0.5 bg-[#2D4A6D] mx-auto" />
          </div>
          <div className="max-w-4xl mx-auto space-y-4">
            {[
              { icon: Search, step: "Understand", desc: "Identify requirements, bottlenecks, priorities, and existing workflows." },
              { icon: FolderOpen, step: "Organize", desc: "Structure information, responsibilities, systems, and processes." },
              { icon: TrendingUp, step: "Optimize", desc: "Remove unnecessary friction and improve operational flow." },
              { icon: Zap, step: "Automate", desc: "Reduce repetitive work and connect systems where practical." },
              { icon: Play, step: "Execute", desc: "Maintain consistent follow-through and operational visibility." }
            ].map((item, i) => (
              <motion.div key={i} initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}>
                <div className="flex items-start gap-6 p-6 bg-white border border-slate-200 hover:border-[#2D4A6D]/30 transition-colors">
                  <div className="w-14 h-14 bg-[#2D4A6D] flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-[#2D4A6D] mb-1">{item.step}</h4>
                    <p className="text-sm text-slate-600">{item.desc}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#2D4A6D] text-white">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <p className="text-2xl sm:text-3xl font-light italic mb-8 font-serif">"Structured operations. Connected systems. Efficient workflows."</p>
          <div className="w-16 h-0.5 bg-white/30 mx-auto mb-10" />
          <p className="text-slate-300 mb-10">Ready to discuss operational requirements and determine the appropriate structure?</p>
          <Link to="/book">
            <button className="bg-white text-[#2D4A6D] hover:bg-slate-100 px-10 py-4 font-medium flex items-center mx-auto">
              Start a Conversation <ArrowRight className="ml-2 w-5 h-5" />
            </button>
          </Link>
        </div>
      </section>
    </div>
  );
};
